import _ from "lodash";
import {
  Button,
  Col,
  Form,
  Row,
  Input,
  theme,
  Typography,
  Image,
  message,
  Space,
} from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage } from "@fortawesome/free-solid-svg-icons";
import { useState, useRef, useEffect } from "react";
import DummyLogo from "../../assets/images/dummy-logo.svg";
import {
  uploadCompanyLogo,
  getCompanyLogo,
} from "../../repository/auth.repository";
import {
  getCompanyProfile,
  updateCompany,
} from "../../repository/data.repository";
import { useSelector, useDispatch } from "../../store/store";
import { authorizeSelectors, authorizeActions } from "../../store/authorize";
import { CompanyProfileInfo } from "../../repository/data.default";
import { useTokenInfo } from "../../hooks/useTokenInfo";
import { getProblemMessage } from "../../utils/http";

export interface CompanyProfileInput {
  readonly name: string;
  readonly address: string;
  readonly phoneNumber: string;
  readonly email: string;
  readonly domainName: string;
  readonly usersCount: number;
}

function CompanyForm() {
  const { claims } = useTokenInfo();
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);
  const [valid, setValid] = useState(false);

  const logoRef = useRef<HTMLInputElement>(null!);
  const [logoLoading, setLogoLoading] = useState(false);
  const [savingChanges, setSavingChanges] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [companyProfile, setCompanyProfile] = useState<
    CompanyProfileInfo | undefined
  >();
  const [initialValues, setInitialValues] = useState<CompanyProfileInput>();
  const [isFormChanged, setIsFormChanged] = useState(false);

  const dispatch = useDispatch();
  const user = useSelector(authorizeSelectors.user);
  const userCompnayUid = claims.usrCmpUid;

  const logoSrc = user?.userCmpLogoSrc ? user?.userCmpLogoSrc : DummyLogo;

  // Lifecycle

  useEffect(() => {
    // Fetch Company Profile
    getCompanyProfile(userCompnayUid)
      .then((profile) => {
        setCompanyProfile(profile);
      })
      .catch((err: any) => {
        messageApi.error(
          getProblemMessage(err) || "Failed to load company profile.",
        );
      });
  }, []);

  useEffect(() => {
    // Fill the form default Values from Company Profile
    if (companyProfile) {
      const formInitialValues = {
        name: companyProfile.name,
        address: companyProfile.address || "",
        phoneNumber: companyProfile.phoneNumber || "",
        email: companyProfile.email || "",
        domainName: companyProfile.domainName,
        usersCount: companyProfile.usersCount || 0,
      };

      form.setFieldsValue(formInitialValues);
      setInitialValues(formInitialValues);
    }
  }, [companyProfile]);

  useEffect(() => {
    //Get Company Logo
    if (!logoLoading) {
      getCompanyLogo(userCompnayUid)
        .catch(() => DummyLogo)
        .then((data) => {
          dispatch(
            authorizeActions.setUser({ ...user, userCmpLogoSrc: data } as any),
          );
        });
    }
  }, [logoLoading]);

  useEffect(() => {
    setIsFormChanged(!_.isEqual(initialValues, values));
    form
      .validateFields()
      .then(() => setValid(true))
      .catch(() => setValid(false));
  }, [values]);

  // Events

  function onSaveChanges(): void {
    const { address, phoneNumber, email } = values;

    setSavingChanges(true);
    updateCompany({
      uid: userCompnayUid,
      rowVersion: companyProfile?.rowVersion!,
      address,
      phoneNumber,
      email,
    })
      .then(() => {
        setSavingChanges(false);
        messageApi.open({
          type: "success",
          content: "Successfully updated company info",
        });
      })
      .catch(() => {
        setSavingChanges(false);
        messageApi.open({
          type: "error",
          content: "Failed to update company info",
        });
      });
  }

  function onCancelChanges(): void {
    form.setFieldsValue(companyProfile);
  }

  function onUploadLogo(e: React.ChangeEvent): void {
    const file = (e.target as any).files[0];
    if (file) {
      const mb = file.size / 1024 / 1024;
      const limit = 10;
      if (mb > limit) {
        messageApi.open({
          type: "error",
          content: "Avatar size limit is 10mb",
        });
      } else {
        setLogoLoading(true);
        uploadCompanyLogo(file, userCompnayUid)
          .then(() => {
            setLogoLoading(false);
            messageApi.open({
              type: "success",
              content: "Successfully uploaded company logo",
            });
          })
          .catch(() => {
            setLogoLoading(false);
            messageApi.open({
              type: "error",
              content: "Failed to upload company logo",
            });
          });
      }
    }
  }

  function onPickLogo(): void {
    logoRef.current.click();
  }

  return (
    <Col className="flex flex-col justify-between">
      <Form
        className="my-form-item flex flex-col px-2 pt-2"
        form={form}
        layout={"vertical"}
        variant={"outlined"}
        colon={false}
        autoComplete="off"
      >
        {/* Avatar */}
        <Row className="flex flex-row justify-center">
          <Col className="relative mb-4 flex flex-col">
            <Image width={"100%"} src={logoSrc} alt="Company Logo" />
            <input
              className="hidden"
              ref={logoRef}
              hidden={true}
              type="file"
              accept="image/png, image/jpeg, image/svg+xml"
              onChange={onUploadLogo}
            />
            <Col className="absolute -bottom-2 -right-2 opacity-50 hover:opacity-100">
              <Button
                shape="circle"
                size="small"
                icon={<FontAwesomeIcon icon={faImage} />}
                loading={logoLoading}
                onClick={onPickLogo}
              />
            </Col>
          </Col>
        </Row>
        {/* Company Name */}
        <Form.Item
          className="m-0.5 w-full"
          name={"name"}
          label={
            <Typography.Text
              className="text-sm"
              style={{
                color: token.colorTextDescription,
              }}
            >
              Company Name
            </Typography.Text>
          }
        >
          <Input disabled={true} style={{ color: "black" }} />
        </Form.Item>
        {/* Company Address */}
        <Form.Item
          className="m-0.5 w-full"
          name={"address"}
          label={
            <Typography.Text
              className="text-sm"
              style={{
                color: token.colorTextDescription,
              }}
            >
              Company Address
            </Typography.Text>
          }
        >
          <Input />
        </Form.Item>
        {/* Phone Number  */}
        <Form.Item
          className="m-0.5 w-full"
          name={"phoneNumber"}
          rules={[
            {
              message: "Phone number is invalid",
              validator: (_, value) => {
                if (!value || /\+?[0-9]+$/.test(value)) {
                  return Promise.resolve();
                } else {
                  return Promise.reject("Phone number is invalid");
                }
              },
            },
          ]}
          label={
            <Typography.Text
              className="text-sm"
              style={{
                color: token.colorTextDescription,
              }}
            >
              Phone Number
            </Typography.Text>
          }
        >
          <Input />
        </Form.Item>
        {/* Email Address */}
        <Form.Item
          className="m-0.5 w-full"
          name={"email"}
          rules={[{ type: "email", message: "Email is invalid" }]}
          label={
            <Typography.Text
              className="text-sm"
              style={{
                color: token.colorTextDescription,
              }}
            >
              Email Address
            </Typography.Text>
          }
        >
          <Input />
        </Form.Item>
        {/* Industry */}
        <Form.Item
          className="m-0.5 w-full"
          name={"domainName"}
          label={
            <Typography.Text
              className="text-sm"
              style={{
                color: token.colorTextDescription,
              }}
            >
              Industry
            </Typography.Text>
          }
        >
          <Input disabled={true} style={{ color: "black" }} />
        </Form.Item>
        {/* Employees */}
        <Form.Item
          className="m-0.5 w-full"
          name={"usersCount"}
          label={
            <Typography.Text
              className="text-sm"
              style={{
                color: token.colorTextDescription,
              }}
            >
              Employees
            </Typography.Text>
          }
        >
          <Input disabled={true} style={{ color: "black" }} />
        </Form.Item>
      </Form>

      <Space className="mb-2 mr-2 mt-1 flex flex-row justify-end">
        <Button
          type="primary"
          loading={savingChanges || logoLoading}
          onClick={onSaveChanges}
          disabled={!isFormChanged || !valid}
        >
          Save
        </Button>
        <Button onClick={onCancelChanges} disabled={!isFormChanged}>
          Cancel
        </Button>
      </Space>

      {contextHolder}
    </Col>
  );
}

export default CompanyForm;
